import { Icon } from 'components/Icon';
import { type JSXChild, type JSXProps, render } from 'engine/index';
import {
	ArrowLeftRight,
	Box,
	Check,
	ChevronLeft,
	ChevronRight,
	CircleHelp,
	Dices,
	DoorClosed,
	Frown,
	Hourglass,
	PartyPopper,
	ThumbsUp,
	Trophy,
	X,
} from 'lucide-static';
import { createRenderLoop, delayMs, lerp, onUnmount, unreachable } from 'shared/util';
import { rpc, State } from 'engine/rpc';
import { type BoardPanelColor, type Card, type JackpotCard, type JackpotUpdate } from 'shared/modelTypes/jackpot';
import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Game } from './game';
import { Timer } from 'components/Timer';
import { marked } from 'marked';
import { signOut } from 'components/Login';
import { showConfetti } from 'engine/utils';
import { BottomRight } from 'components/BottomRight';
import { SelectorButton } from 'components/SelectorButton';
import { resolveWordIcon } from './jackpot-data';

export class Jackpot extends Game<'jackpot-van-las-vegas'> {
	override maxPlayers: number = 6;

	shellGoodCardEl!: HTMLElement;
	shellBadCardEl!: HTMLElement;
	shellDesireCardEl!: HTMLElement;
	shellLotteryCardEl!: HTMLElement;
	shellScoreEl!: HTMLElement;

	// player state
	name: string = '';
	color: string = '';
	goodCards: JackpotCard[] = [];
	badCards: JackpotCard[] = [];
	desireCards: JackpotCard[] = [];
	lotteryTickets: JackpotCard[] = [];
	score: number = 0;

	override getGameName(): string {
		return __`jackpot.gameTitle`;
	}

	override defineBottomUI(): JSXChild {
		return (
			<>
				<StatBubble
					onClick={() => showModal(<CardList label={__`jackpot.lottery`} cards={this.lotteryTickets} />)}
					color="purple"
					label={__`jackpot.lottery`}
					value="0"
					id="lottery-tickets-count"
				/>
				<StatBubble
					onClick={() => showModal(<CardList label={__`jackpot.goodCards`} cards={this.goodCards} />)}
					color="yellow"
					label={__`jackpot.goodCards`}
					value="0"
					id="good-card-count"
				/>
				<StatBubble
					onClick={() => showModal(<CardList label={__`jackpot.badCards`} cards={this.badCards} />)}
					color="red"
					label={__`jackpot.badCards`}
					value="0"
					id="bad-card-count"
				/>
				<StatBubble
					onClick={() => showModal(<CardList label={__`jackpot.desireCards`} cards={this.desireCards} />)}
					color="green"
					label={__`jackpot.desireCards`}
					value="0"
					id="desire-card-count"
				/>
				<StatBubble color="green" label={__`Score`} value="0" id="score" />
			</>
		);
	}

	override onShellCreated(view: HTMLElement): void {
		super.onShellCreated(view);

		this.shellGoodCardEl = view.querySelector('#good-card-count')!;
		this.shellBadCardEl = view.querySelector('#bad-card-count')!;
		this.shellDesireCardEl = view.querySelector('#desire-card-count')!;
		this.shellLotteryCardEl = view.querySelector('#lottery-tickets-count')!;
		this.shellScoreEl = view.querySelector('#score')!;
	}

	override handleUpdate(update: JackpotUpdate): void {
		const sessionId = State.gameId.toString();

		const playerId = State.playerId;

		switch (update.kind) {
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				this.showRegisterScreen();
				break;
			case 'show-round-intro-screen':
				render(this.shellContentEl, <RoundIntro round={update.round} roundName={update.roundName} />);
				break;
			case 'show-round-outro-screen':
				render(
					this.shellContentEl,
					<RoundOutro
						score={update.score}
						round={update.round}
						roundName={update.roundName}
						jackpot={update.jackpot}
						options={update.options}
					/>,
				);
				break;
			case 'show-turnstart-screen':
				render(
					this.shellContentEl,
					<ChooseLandingSpot
						onChosen={(color) =>
							rpc.sendJackpotMessage.query({
								sessionId,
								update: {
									kind: 'choose-landing-spot',
									playerId,
									panelColor: color,
								},
							})
						}
					/>,
				);
				break;
			case 'show-swipecards-screen':
				{
					const title = [
						__`jackpot.round1 ${update.roundTitle}`,
						__`jackpot.round2 ${update.roundTitle}`,
						__`jackpot.round3 ${update.roundTitle}`,
					][update.round - 1];

					render(
						this.shellContentEl,
						<ChooseCards
							onBoardPanel={update.forColor}
							title={title}
							options={update.options}
							onChosen={(chosen, swipedCount) => {
								rpc.sendJackpotMessage.query({
									sessionId,
									update: {
										kind: 'choose-cards',
										playerId,
										cards: chosen,
										swipedCount,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'show-jackpot-guess-screen':
				render(
					this.shellContentEl,
					<ChooseJackpotLandingScreen
						sabotage={false}
						options={update.options}
						onChosen={(landing) => {
							rpc.sendJackpotMessage.query({
								sessionId,
								update: {
									kind: 'choose-jackpot-landing',
									playerId,
									landing,
								},
							});
						}}
					/>,
				);
				break;
			case 'show-sabotage-screen':
				render(
					this.shellContentEl,
					<ChooseJackpotLandingScreen
						options={update.options}
						sabotage={true}
						onChosen={(landing) => {
							rpc.sendJackpotMessage.query({
								sessionId,
								update: {
									kind: 'choose-sabotage-landing',
									playerId,
									landing,
								},
							});
						}}
					/>,
				);
				break;
			case 'show-randomevent-screen':
				render(
					this.shellContentEl,
					<RandomEvent
						action={update.action}
						chooseColor={update.chooseColor}
						tickets={update.tickets}
						onChosen={(color) =>
							rpc.sendJackpotMessage.query({
								sessionId,
								update: {
									kind: 'choose-landing-spot',
									playerId,
									panelColor: color,
								},
							})
						}
					/>,
				);
				break;
			case 'show-wait-screen':
				render(this.shellContentEl, <WaitForTurn playerName={update.playerName} />);
				break;
			case 'show-results-screen':
				render(this.shellContentEl, <ResultsScreen results={update.players} />);
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
				break;
			case 'update-player-state':
				// console.log('update player', update);
				this.name = update.player.name;
				this.color = update.player.color;
				this.goodCards = update.player.goodCards;
				this.badCards = update.player.badCards;
				this.desireCards = update.player.desireCards;
				this.lotteryTickets = update.player.lotteryTickets;
				this.score = update.player.score;

				this.shellNameEl.innerText = this.name ?? 'Geen naam';
				this.shellGoodCardEl.innerText = this.goodCards.length.toString();
				this.shellBadCardEl.innerText = this.badCards.length.toString();
				this.shellDesireCardEl.innerText = this.desireCards.length.toString();
				this.shellLotteryCardEl.innerText = this.lotteryTickets.length.toString();
				this.shellScoreEl.innerText = this.score.toString();
				setUserIconColor(this.userIconEl, this.color);

				break;
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			default:
				unreachable(update);
		}
	}
}

export function RoundIntro({ roundName, round }: { roundName: string; round: number }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">
				<span>Ronde {round}</span>
				<h2 class="text-3xl">{roundName}</h2>
				<p>Wacht op de spelleider om de ronde te starten.</p>
			</div>
		</div>
	);
}

export function RoundOutro({
	roundName,
	round,
	jackpot,
	options,
	score,
}: {
	roundName: string;
	round: number;
	jackpot: string[];
	options?: string[];
	score: number;
}) {
	function easeOutCubic(x: number): number {
		return 1 - Math.pow(1 - x, 3);
	}

	delayMs(8_500).then(() => {
		if (score) showConfetti();
		scoreView.style.opacity = '';
	});

	const view: HTMLElement = (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-gray-800">
				<span class="text-xl">Eind ronde {round}</span>
				<h2 class="text-3xl">{roundName}</h2>
				<p>Nu gaan we kijken waar de jackpot op is gevallen.</p>
			</div>
			<div class="mx-auto flex w-full flex-col items-center justify-center gap-5">
				{/* If no options are passed this means we render fruits */}
				{!options &&
					jackpot.map((v, i) => {
						const fruitIndex = parseInt(v.replace('fruit-', ''));

						const view: HTMLElement = (
							<div
								class="flex size-20 border border-gray-500 bg-[url(/jackpot/slotreel.webp)] bg-cover shadow-md"
								style={`background-position: 0px ${(100 / 8) * fruitIndex}%`}
							></div>
						);

						const targetOffset = (100 / 8) * fruitIndex;

						const startOffset = targetOffset + 800;
						const totalTime = 10 + i * 0.5;
						let time = 0;
						createRenderLoop((dt) => {
							time += dt;

							const t = time / totalTime;
							const v = lerp(startOffset, targetOffset, easeOutCubic(t));

							view.style.backgroundPosition = `0px ${v}%`;

							return time <= totalTime;
						});

						return view;
					})}

				{/* If options are passed we cycle between those word options and land on the correct one */}
				{options &&
					jackpot.map((v, i) => {
						const icon = resolveWordIcon(v);

						const view: HTMLElement = (
							<div class="w-full rounded-md border border-gray-500 p-4 text-center font-bold shadow-md">
								<div class="flex items-center space-x-4">
									<Icon icon={icon || Box} class="size-16 text-gray-300" />
									<p class="text-xl">{v}</p>
								</div>
							</div>
						);

						const targetOffset = Math.max(0, options.indexOf(v));

						const startOffset = targetOffset + 80;
						const totalTime = 10 + i * 0.5;
						let time = 0;
						createRenderLoop((dt) => {
							time += dt;

							const t = time / totalTime;
							const v = lerp(startOffset, targetOffset, easeOutCubic(t));
							const label = options[Math.floor(v) % options.length];
							const icon = resolveWordIcon(label);
							view.innerText = options[Math.floor(v) % options.length];
							render(
								view,
								<div class="flex items-center space-x-4">
									<Icon icon={icon || Box} class="size-16 text-gray-300" />
									<p class="text-xl">{label}</p>
								</div>,
							);

							return time <= totalTime;
						});

						return view;
					})}
			</div>
			<p class="text-center" score-view style="opacity:0">
				{score > 0 && __`jackpot.resultGood ${score}`}
				{score <= 0 && __`jackpot.resultBad`}
			</p>
		</div>
	);
	const scoreView = view.querySelector('[score-view]') as HTMLElement;

	return view;
}

// step 4 (roll dice and choose landing spot color)
export function ChooseLandingSpot({ onChosen }: { onChosen: (chosen: BoardPanelColor) => void }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">{__`jackpot.chooseLandingSpot`}</div>
			<div class="grid grid-cols-2 gap-4 md:grid-cols-3">
				<BoardPanel onSelect={onChosen} kind="yellow" />
				<BoardPanel onSelect={onChosen} kind="red" />
				<BoardPanel onSelect={onChosen} kind="green" />
				<BoardPanel onSelect={onChosen} kind="blue" />
				<BoardPanel onSelect={onChosen} kind="purple" />
				<BoardPanel onSelect={onChosen} kind="orange" />
			</div>
		</div>
	);
}

// step 4 (choose card - red/yellow)
export function ChooseCards({
	onBoardPanel,
	options,
	title,
	onChosen,
}: {
	onBoardPanel: 'red' | 'yellow' | 'green';
	options: Card[];
	title: string;
	onChosen?: (chosen: string[], swipeCount: number) => void;
}) {
	const chosen: string[] = [];
	let swipeCount = 0;

	const DragCard = (props: JSXProps, _children: JSXChild[]) => {
		const icon = resolveWordIcon(props.label as string, props.iconName as string | undefined);

		const view: HTMLElement = (
			<div
				card
				data-value={props.label}
				class="w-60 max-w-full transition-transform duration-500"
				style={`--i:${props.index}; grid-area: 1 / 1; z-index:${80 - props.index};`}
			>
				<div
					style={`transform:translateY(calc(var(--i)*4px)); transition-duration:calc(100ms + 200ms * var(--i))`}
					class="relative w-full rounded-md border border-gray-900 bg-white p-6 text-center text-sm shadow-md transition-transform md:text-base"
				>
					<Icon icon={icon || Box} class="mb-4 size-36 w-full border-b-2 pb-4 text-gray-300" />
					<p class="h-10 text-lg font-bold">{props.label}</p>
				</div>
			</div>
		);
		return view;
	};

	const view: HTMLElement = (
		<div class="flex w-fit max-w-screen-lg select-none flex-col p-6">
			<div class="mb-4 flex flex-row items-center gap-4">
				<BoardPanel small kind={onBoardPanel} />
				<div class="text-xl text-gray-800">
					<span>{__`jackpot.chooseCards`}</span>
					<br />
					<span>{title}</span>
					<br />
					{onBoardPanel === 'yellow' && __`jackpot.chooseGood`}
					{onBoardPanel === 'red' && __`jackpot.chooseBad`}
					{onBoardPanel === 'green' && __`jackpot.chooseDesire`}
				</div>
				<BottomRight>
					<Timer timerFrom={Date.now()} timerTo={Date.now() + 1000 * 15} />
				</BottomRight>
			</div>
			<div class="flex items-center">
				<Icon icon={ChevronLeft} class="size-10 text-gray-200" />
				<div card-container class="grid flex-grow place-items-center">
					{options.map((v, i) => {
						return <DragCard label={v.name} iconName={v.icon} index={i} />;
					})}
				</div>
				<Icon icon={ChevronRight} class="size-10 text-gray-200" />
			</div>
			<div class="mt-14 flex flex-1 shrink items-center justify-between">
				<button
					on:click={() => {
						cardLeft();
					}}
					class="z-[90] flex aspect-square size-20 items-center justify-center rounded-lg border border-red-600 bg-red-500 p-3 text-center text-xs text-red-50"
				>
					<Icon icon={X} class="size-10 text-white" />
				</button>

				<button
					on:click={() => {
						cardRight();
					}}
					class="z-[90] flex aspect-square size-20 items-center justify-center rounded-lg border border-green-600 bg-green-500 p-3 text-center text-xs text-green-50"
				>
					<Icon icon={Check} class="size-10 text-white" />
				</button>
			</div>
		</div>
	);
	const cardContainer = view.querySelector('[card-container]') as HTMLElement;

	let startX: number, startTime: number;
	if (onChosen) {
		setTimeout(() => {
			onChosen(chosen, swipeCount);
		}, 15000);
	}

	const grabCard = () => {
		return cardContainer.querySelector('[card]:not([dragged])') as HTMLElement;
	};

	const cleanupCard = async (card: HTMLElement) => {
		const remainingCards = cardContainer.querySelectorAll('[card]:not([dragged])') as NodeListOf<HTMLElement>;
		let i = 0;
		for (const card of remainingCards) {
			card.style.setProperty('--i', i.toString());
			i++;
		}

		await delayMs(300);

		await card.animate([{ opacity: '1' }, { opacity: '0' }], {
			easing: 'ease-out',
			duration: 300,
		}).finished;

		card.remove();
	};

	const cardLeft = () => {
		const card = grabCard();
		if (!card) return;

		const val = card.getAttribute('data-value') as string;
		if (val) {
			// chosen.left.push(val);
			swipeCount++;
		}

		card.setAttribute('dragged', '');
		card.style.transform = 'perspective(800px) translateX(-50vw) scale(0.8) rotate3d(1, 1, 1, -20deg)';
		cleanupCard(card);
	};

	const cardRight = () => {
		const card = grabCard();
		if (!card) return;

		const val = card.getAttribute('data-value') as string;
		if (val) {
			chosen.push(val);
			swipeCount++;
		}

		card.setAttribute('dragged', '');
		card.style.transform = 'perspective(800px) translateX(50vw) scale(0.8) rotate3d(1, 1, 1, 20deg)';
		cleanupCard(card);
	};

	const onTouchStart = (e: TouchEvent) => {
		startX = e.touches[0].clientX;
		startTime = Date.now();
	};

	const onTouchEnd = (e: TouchEvent) => {
		const endX = e.changedTouches[0].clientX;
		const deltaX = endX - startX;
		const deltaTime = Date.now() - startTime;

		if (deltaTime < 500) {
			// Limit for swipe speed
			if (deltaX > 50) {
				cardRight();
			} else if (deltaX < -50) {
				cardLeft();
			}
		}
	};

	const onMouseDown = (e: MouseEvent) => {
		startX = e.clientX;
		startTime = Date.now();
	};

	const onMouseUp = (e: MouseEvent) => {
		const endX = e.clientX;
		const deltaX = endX - startX;
		const deltaTime = Date.now() - startTime;

		// console.log(deltaX, deltaTime);
		if (deltaTime < 500) {
			if (deltaX > 50) {
				cardRight();
			} else if (deltaX < -50) {
				cardLeft();
			}
		}
	};

	document.addEventListener('touchstart', onTouchStart);
	document.addEventListener('touchend', onTouchEnd);
	document.addEventListener('mousedown', onMouseDown);
	document.addEventListener('mouseup', onMouseUp);

	onUnmount(view, () => {
		document.removeEventListener('touchstart', onTouchStart);
		document.removeEventListener('touchend', onTouchEnd);
		document.removeEventListener('mousedown', onMouseDown);
		document.removeEventListener('mouseup', onMouseUp);
	});

	return view;
}

export const ChooseJackpotLandingScreen = ({
	options,
	sabotage,
	onChosen,
}: {
	options: string[];
	sabotage: boolean;
	onChosen: (chosen: string) => void;
}) => {
	const fruitView = options[0].startsWith('fruit-');

	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<BoardPanel kind={sabotage ? 'orange' : 'purple'} />
			<div
				class="text-center text-xl text-gray-800"
				__innerHTML={marked(sabotage ? __`jackpot.sabotageLanding` : __`jackpot.guessLanding`)}
			></div>
			{!fruitView && (
				<div class="flex flex-col gap-5">
					{options.map((v) => (
						<SelectorButton on:click={() => onChosen(v)}>{v}</SelectorButton>
					))}
				</div>
			)}
			{fruitView && (
				<div class="grid grid-cols-3 gap-5">
					{options.map((v) => (
						<button
							class="overflow-hidden rounded-md border border-gray-300 text-theme-50 shadow-md"
							on:click={() => onChosen(v)}
						>
							<div
								class="size-16 bg-[url(/jackpot/slotreel.webp)] bg-cover"
								style={`background-position: 0px ${(100 / 8) * parseInt(v.replace('fruit-', ''))}%`}
							></div>
						</button>
					))}
				</div>
			)}
		</div>
	);
};

const fruitDisplay = (label: string) => {
	const fruitIndex = parseInt(label.replace('fruit-', ''));

	return (
		<div
			class="size-20 border border-gray-500 bg-[url(/jackpot/slotreel.webp)] bg-cover shadow-md"
			style={`background-position: 0px ${(100 / 8) * fruitIndex}%`}
		></div>
	);
};

const cardDisplay = (label: string) => {
	const icon = resolveWordIcon(label);

	return (
		<div class="size-[140px] shrink-0 rounded-md border border-gray-100 bg-gray-50 shadow-inner md:h-[160px] md:w-[140px]">
			<div
				class={
					'flex size-full flex-col items-center justify-between rounded-md border-2 bg-white px-6 py-4 shadow-sm transition-all data-[taken]:pointer-events-none data-[taken]:opacity-30'
				}
			>
				<Icon icon={icon || Box} class="mb-2 size-36 w-full border-b-2 pb-4 text-gray-300" />
				<p class="pointer-events-none text-center text-xs font-semibold md:text-lg">{label}</p>
			</div>
		</div>
	);
};

export function RandomEvent({
	action,
	chooseColor,
	tickets,
	onChosen,
}: {
	action: string;
	chooseColor: boolean;
	tickets: { name: string }[] | undefined;
	onChosen: (chosen: BoardPanelColor) => void;
}) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<BoardPanel kind={'blue'} />
			<div class="text-center text-xl text-gray-800">
				{__`jackpot.randomEvent.intro`}
				<p class="font-bold">{action}</p>
			</div>
			{chooseColor && (
				<div class="grid grid-cols-2 gap-4 md:grid-cols-3">
					<BoardPanel onSelect={onChosen} kind="yellow" />
					<BoardPanel onSelect={onChosen} kind="red" />
					<BoardPanel onSelect={onChosen} kind="green" />
					<BoardPanel onSelect={onChosen} kind="blue" />
					<BoardPanel onSelect={onChosen} kind="purple" />
					<BoardPanel onSelect={onChosen} kind="orange" />
				</div>
			)}
			{tickets &&
				tickets.length > 0 &&
				(tickets[0].name.startsWith('fruit-') ? tickets.map((t) => fruitDisplay(t.name)) : tickets.map((t) => cardDisplay(t.name)))}
		</div>
	);
}

// step 4 (not your turn)
export function WaitForTurn({ playerName }: { playerName: string }) {
	return (
		<div class="flex flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
			<div
				class="text-center text-xl text-gray-800"
				__innerHTML={marked(__`jackpot.waitForTurn.action ${'**' + playerName + '**'}`)}
			></div>
		</div>
	);
}

export function JackpotWait() {
	return (
		<div class="flex flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">{__`jackpot.wait`}</div>
		</div>
	);
}

export function JackpotResult() {
	return (
		<div class="flex flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={PartyPopper} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">Gefeliciteerd je hebt de jackpot gewonnen en je ontvangt 5 punten!</div>
		</div>
	);
}

export function ResultsScreen({ results }: { results: { name: string; color: string; score: number }[] }) {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Einduitslag</h2>
			<table class="overflow-hidden rounded-md shadow-sm">
				<thead>
					<tr class="bg-gray-100">
						<th class="px-5 py-1">Rank</th>
						<th class="px-5 py-1">Pion</th>
						<th class="px-5 py-1">Naam</th>
						<th class="px-5 py-1">Punten</th>
					</tr>
				</thead>
				<tbody>
					{results
						.sort((a, b) => b.score - a.score)
						.map((r, i) => {
							const highlight = State.color == r.color;

							return (
								<tr class={highlight ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'even:bg-gray-50'}>
									<td class="px-5 py-1">
										<div class="inline-flex items-baseline gap-3">
											<span class="font-bold">{i + 1}</span>
											{i == 0 && <Icon icon={Trophy} class="size-4 text-orange-400" />}
											{i == 1 && <Icon icon={Trophy} class="size-4 text-gray-400" />}
											{i == 2 && <Icon icon={Trophy} class="size-4 text-orange-700" />}
											{i >= 3 && <Icon icon={Trophy} class="size-4 opacity-0" />}
										</div>
									</td>
									<td class="px-5 py-1">{__(['playerColor.' + r.color])}</td>
									<td class="px-5 py-1">{r.name}</td>
									<td class="px-5 py-1">{r.score}</td>
								</tr>
							);
						})}
				</tbody>
			</table>
			<div class="flex gap-3">
				<button
					class="rounded-md bg-theme-600 p-4 text-theme-50"
					on:click={async () => {
						const res = await rpc.getReportForSession.query({ gameId: State.gameId, sessionId: State.sessionId });
						if (!('success' in res) || !res.success) {
							console.error(res.error);
							return;
						}
						const path = res.reportUrl;
						const url = new URL(path, window.location.origin);
						const link = document.createElement('a');
						link.href = url.toString();
						link.download = path.split('/').pop() || 'download';
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					}}
				>
					Download rapport
				</button>
				<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
					Spel afsluiten
				</button>
			</div>
		</div>
	);
}

function BoardPanel({ kind, small, onSelect }: { small?: boolean; kind: BoardPanelColor; onSelect?: (color: BoardPanelColor) => void }) {
	let _icon: string;
	let colorClasses: string;
	switch (kind) {
		case 'yellow':
			_icon = ThumbsUp;
			colorClasses = 'bg-yellow-400 text-yellow-900 border-yellow-500';
			break;
		case 'green':
			_icon = CircleHelp;
			colorClasses = 'bg-green-500 text-green-50 border-green-600';
			break;
		case 'red':
			_icon = Frown;
			colorClasses = 'bg-red-500 text-red-50 border-red-600';
			break;
		case 'blue':
			_icon = ArrowLeftRight;
			colorClasses = 'bg-blue-500 text-blue-50 border-blue-600';
			break;
		case 'purple':
			_icon = Dices;
			colorClasses = 'bg-purple-500 text-purple-50 border-purple-600';
			break;
		case 'orange':
			_icon = DoorClosed;
			colorClasses = 'bg-orange-500 text-orange-50 border-orange-600';
			break;
	}

	const selectionClasses = onSelect ? 'cursor-pointer hover:scale-105 hover:opacity-95 active:scale-95 active:opacity-95 ' : '';
	return (
		<div
			data-small={small}
			class={'aspect-square rounded-sm border p-6 transition-transform data-[small]:p-2 md:p-8 ' + selectionClasses + colorClasses}
			on:click={!onSelect ? undefined : () => onSelect(kind)}
		>
			<div class="size-12 data-[small]:size-10 md:size-16"></div>
			{/* <Icon icon={icon} class="size-12 data-[small]:size-10 md:size-16" /> */}
		</div>
	);
}

function SwipeCard({ label }: { label: string }) {
	return label.startsWith('fruit-') ? fruitDisplay(label) : cardDisplay(label);
}

export function CardList({ label, cards }: { label: string; cards: JackpotCard[] }) {
	return (
		<div class="relative flex max-h-full w-full max-w-md flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg">
			<button action-cancel class="absolute right-2 top-2 rounded-md p-2">
				<Icon icon={X} class="size-6" />
			</button>
			<h4 class="mb mb-6 font-bold text-gray-900">{_k(label)}</h4>
			<div class="ju grid w-full flex-1 shrink grid-cols-2 place-items-center gap-4 overflow-y-auto">
				{cards.map((c) => (
					<SwipeCard label={c.name} />
				))}
			</div>
			{!cards.length && <small class="text-center">{__`jackpot.cardList.noCards`}</small>}
		</div>
	);
}
